import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import {Grid} from '@mui/material';

import {Dispatch, Store} from 'spectra-logic-ui';
import {fetchResource} from 'spectra-logic-ui/actions';
import {SlidePanel} from 'spectra-logic-ui/components';

import {Aggregate, Pool} from '@/types';
import Aggregates from '@/storage/aggregates';
import CloudStorage from '@/storage/cloud_storage';
import OnSiteStorage from '@/storage/on_site_storage';
import AggregateProperties from '@/storage/details/aggregate_properties';
import PropertiesDetails from '@/storage/details/properties';
import MetricsDetails from '@/storage/details/metrics';
import {StorageRowId} from '@/storage/types';

type Props = {
  pools: Pool[];
  aggregates: Aggregate[];
  fetchPools: Function;
  fetchAggregates: Function;
  fetching: boolean;
}

const Storage = ({pools=[], aggregates, fetchPools, fetchAggregates, fetching}: Props) => {
  const [selectedRowId, selectRowId] = React.useState({} as StorageRowId);
  const [isOpenDetails, setOpenDetails] = React.useState(false);
  const closeDetails = () => setOpenDetails(false);
  const clearSelectedRow = () => {
    setOpenDetails(false);
    selectRowId({} as StorageRowId);
  };

  useEffect(() => {
    fetchPools();
    fetchAggregates();
  }, []);

  const selectedPool = pools.find((pool) => pool.id === selectedRowId.poolId);
  const metricsId = selectedPool ? selectedPool.exporter ? '' : selectedPool.id : '';

  const selectedAggregate = aggregates.find((ag) => ag.id === selectedRowId.aggregateId);

  const slideTitle = (selectedPool || selectedAggregate || {}).name || '';
  const slideOptions = selectedPool ? ['Properties', 'Usage'] : ['Properties'];

  let slideTabs;
  if (selectedPool) {
    slideTabs = [
      <PropertiesDetails storage={selectedPool || {} as Pool} />,
      <MetricsDetails id={metricsId} usedBy={selectedPool.usedBy}/>,
    ];
  } else {
    slideTabs = [<AggregateProperties aggregate={selectedAggregate || {} as Aggregate} />];
  }

  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={12}>
          <OnSiteStorage
            pools={pools} selectedPool={selectedPool} selectedRowId={selectedRowId} selectRowId={selectRowId}
            clearSelectedRow={clearSelectedRow} setOpenDetails={setOpenDetails} closeDetails={closeDetails}
            fetchingPools={fetching} />
        </Grid>
        <Grid item xs={12}>
          <CloudStorage
            pools={pools} selectedPool={selectedPool} selectedRowId={selectedRowId} selectRowId={selectRowId}
            clearSelectedRow={clearSelectedRow} setOpenDetails={setOpenDetails} fetchingPools={fetching} />
        </Grid>
        <Grid item xs={12}>
          <Aggregates
            aggregates={aggregates} pools={pools} selectedAggregate={selectedAggregate} selectedRowId={selectedRowId}
            selectRowId={selectRowId} clearSelectedRow={clearSelectedRow} setOpenDetails={setOpenDetails}
            fetching={fetching} />
        </Grid>
      </Grid>
      <SlidePanel title={slideTitle} options={slideOptions} open={isOpenDetails} onClose={closeDetails}>
        {...slideTabs}
      </SlidePanel>
    </React.Fragment>
  );
};

const mapStateToProps = (state: Store) => {
  const pools = state.resources.storage || {};
  const aggregates = state.resources.aggregates || {};
  return {
    pools: pools.data || [],
    aggregates: aggregates.data || [],
    fetching: pools.fetching || aggregates.fetching,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchPools: () => dispatch(fetchResource('storage')),
  fetchAggregates: () => dispatch(fetchResource('aggregates')),
});

export default connect(mapStateToProps, mapDispatchToProps)(Storage);
